import type Phaser from 'phaser';

export interface BowlDeps {
  scene: Phaser.Scene;
  /** Center x of the bowl in scene coords. */
  x: number;
  /** Y of the bowl's interior top (rim) in scene coords. */
  interiorTopY: number;
  /** Y of the bowl's interior bottom (floor) in scene coords. */
  interiorBottomY: number;
  /** Interior opening width in scene coords. */
  interiorWidth: number;
}

// CSS-px design values — multiplied by dpr (from registry) at construction so
// the bowl renders crisply on HiDPI while keeping the same visual size.
const WALL_THICKNESS_CSS = 24;
const CORNER_RADIUS_CSS = 18;
const RIM_OFFSET_CSS = 6;
const RIM_HEIGHT_CSS = 9;
const RIM_CORNER_CSS = 4;

// Ceramic-bowl palette. Each surface gets a vertical 2-stop gradient so the
// flat Graphics shapes read as dimensional pottery rather than coloured rects.
// Walls darken top→bottom; floor goes light→darker for a "scooped" feel.
const WALL_TOP = 0xffc9b8;
const WALL_BOTTOM = 0xe88f78;
const FLOOR_TOP = 0xffe5d6;
const FLOOR_BOTTOM = 0xf4baa0;
const RIM_TOP = 0xffaa92; // catches light on the cap's upper face
const RIM_BOTTOM = 0xf17a60; // shadow underside, where rim meets wall
const RIM_GLOSS = 0xffe1d2; // 1-pixel hairline along the very top — ceramic gloss
const INNER_SHADOW = 0x6b2e22; // rim casts shadow into the interior — low alpha
const GROUND_SHADOW = 0x000000; // soft ellipse under the bowl, low alpha

/**
 * Bowl is drawn with Phaser.Graphics — no sprite asset. Three Matter static
 * rectangles back it (left wall, right wall, floor).
 */
export class Bowl {
  readonly graphics: Phaser.GameObjects.Graphics;
  readonly walls: MatterJS.BodyType[];
  readonly dangerLineY: number;
  readonly interiorTopY: number;
  readonly interiorLeftX: number;
  readonly interiorRightX: number;
  readonly interiorBottomY: number;
  readonly viewportHeight: number;

  constructor(deps: BowlDeps) {
    const { scene } = deps;
    const matter = scene.matter;
    const dpr = (scene.registry.get('dpr') as number) ?? 1;
    const WALL_THICKNESS = WALL_THICKNESS_CSS * dpr;
    const CORNER_RADIUS = CORNER_RADIUS_CSS * dpr;
    const RIM_OFFSET = RIM_OFFSET_CSS * dpr;
    const RIM_HEIGHT = RIM_HEIGHT_CSS * dpr;
    const RIM_CORNER = RIM_CORNER_CSS * dpr;

    this.interiorLeftX = deps.x - deps.interiorWidth / 2;
    this.interiorRightX = deps.x + deps.interiorWidth / 2;
    this.interiorTopY = deps.interiorTopY;
    this.interiorBottomY = deps.interiorBottomY;

    const wallHeight = deps.interiorBottomY - deps.interiorTopY + WALL_THICKNESS;
    const wallCenterY = deps.interiorTopY + (deps.interiorBottomY - deps.interiorTopY) / 2;
    const floorY = deps.interiorBottomY + WALL_THICKNESS / 2;
    const floorWidth = deps.interiorWidth + WALL_THICKNESS * 2;

    this.walls = [
      matter.add.rectangle(
        this.interiorLeftX - WALL_THICKNESS / 2,
        wallCenterY,
        WALL_THICKNESS,
        wallHeight,
        { isStatic: true, label: 'bowl-wall-left' },
      ),
      matter.add.rectangle(
        this.interiorRightX + WALL_THICKNESS / 2,
        wallCenterY,
        WALL_THICKNESS,
        wallHeight,
        { isStatic: true, label: 'bowl-wall-right' },
      ),
      matter.add.rectangle(deps.x, floorY, floorWidth, WALL_THICKNESS, {
        isStatic: true,
        label: 'bowl-floor',
      }),
    ];

    // ─── Layered draw (back→front) for a ceramic-bowl look ───
    const g = scene.add.graphics();
    this.graphics = g;

    const leftWallX = this.interiorLeftX - WALL_THICKNESS;
    const rightWallX = this.interiorRightX;
    const floorX = deps.x - floorWidth / 2;
    const wallHeightTotal = deps.interiorBottomY - deps.interiorTopY + WALL_THICKNESS;
    const innerShadowH = 8 * dpr;

    // 1. Ground shadow — soft ellipse beneath the bowl. Grounds the bowl on
    //    the bg so it doesn't look like it's floating.
    g.fillStyle(GROUND_SHADOW, 0.14);
    g.fillEllipse(
      deps.x,
      deps.interiorBottomY + WALL_THICKNESS + 6 * dpr,
      floorWidth * 0.94,
      WALL_THICKNESS * 0.7,
    );

    // 2. Floor — light→darker vertical gradient, rounded base.
    g.fillGradientStyle(FLOOR_TOP, FLOOR_TOP, FLOOR_BOTTOM, FLOOR_BOTTOM, 1);
    g.fillRoundedRect(floorX, deps.interiorBottomY, floorWidth, WALL_THICKNESS, {
      tl: 0,
      tr: 0,
      bl: CORNER_RADIUS,
      br: CORNER_RADIUS,
    });

    // 3. Walls — top-lighter, bottom-darker vertical gradient. Same gradient
    //    on both walls so they read as one continuous bowl side.
    g.fillGradientStyle(WALL_TOP, WALL_TOP, WALL_BOTTOM, WALL_BOTTOM, 1);
    g.fillRoundedRect(leftWallX, deps.interiorTopY, WALL_THICKNESS, wallHeightTotal, {
      tl: CORNER_RADIUS,
      tr: 0,
      bl: CORNER_RADIUS,
      br: 0,
    });
    g.fillRoundedRect(rightWallX, deps.interiorTopY, WALL_THICKNESS, wallHeightTotal, {
      tl: 0,
      tr: CORNER_RADIUS,
      bl: 0,
      br: CORNER_RADIUS,
    });

    // 4. Rim caps — thicker, gradient-shaded (light top → shadow underside),
    //    sitting just above the rim line. This is the "lip" of the bowl.
    g.fillGradientStyle(RIM_TOP, RIM_TOP, RIM_BOTTOM, RIM_BOTTOM, 1);
    g.fillRoundedRect(leftWallX, deps.interiorTopY - RIM_OFFSET, WALL_THICKNESS, RIM_HEIGHT, {
      tl: RIM_CORNER,
      tr: 0,
      bl: 0,
      br: 0,
    });
    g.fillRoundedRect(rightWallX, deps.interiorTopY - RIM_OFFSET, WALL_THICKNESS, RIM_HEIGHT, {
      tl: 0,
      tr: RIM_CORNER,
      bl: 0,
      br: 0,
    });

    // 5. Rim gloss — a 1px hairline of warm cream along the very top edge of
    //    each rim cap. This is what sells the ceramic feel: a sharp specular
    //    line catching light, instead of the rim reading as a flat block.
    g.fillStyle(RIM_GLOSS, 0.85);
    g.fillRect(leftWallX + RIM_CORNER, deps.interiorTopY - RIM_OFFSET, WALL_THICKNESS - RIM_CORNER, dpr);
    g.fillRect(rightWallX, deps.interiorTopY - RIM_OFFSET, WALL_THICKNESS - RIM_CORNER, dpr);

    // 6. Inner rim shadow — a soft strip just inside the interior, top edge.
    //    Reads as the shadow the rim casts down into the bowl. Fades quickly
    //    so it doesn't fight with the fruits inside.
    g.fillGradientStyle(INNER_SHADOW, INNER_SHADOW, INNER_SHADOW, INNER_SHADOW, 0.22, 0.22, 0, 0);
    g.fillRect(this.interiorLeftX, deps.interiorTopY, deps.interiorWidth, innerShadowH);

    g.setDepth(5);

    this.viewportHeight = scene.scale.height;
    // Danger line sits at the bowl rim. Grace timer means a single bounce
    // above the line doesn't immediately end the game.
    this.dangerLineY = deps.interiorTopY;
  }

  destroy(): void {
    const scene = this.graphics.scene;
    this.graphics.destroy();
    for (const w of this.walls) scene.matter.world.remove(w);
  }
}
